import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button, Container, Modal, ModalHeader, ModalBody, ModalFooter, ListGroup, ListGroupItem } from 'reactstrap';
import { sendVirtualPageView } from '../../../shared/utility';
import useJuryData from '../JuryProjectsList/useJuryData';
import useJuryStore from '../../../store/juryStore';
import { useJuryFundingOptions } from '../../../store/configStore';
import JuryProjectsGrid from '../JuryProjectsList/JuryProjectsGrid';
import styles from './JuryFinalVoteSubmission.module.scss';
import { IconCheck } from '@tabler/icons-react';

function JuryFinalVoteSubmission() {
    useJuryData(); // triggers one-time fetch

    const projects = useJuryStore((s) => s.projects);
    const poll = useJuryStore((s) => s.poll);
    const fundingOptions = useJuryFundingOptions();
    const isConfirmed = useJuryStore((s) => s.isConfirmed);
    const confirmVotes = useJuryStore((s) => s.confirmVotes);

    const [showModal, setShowModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        sendVirtualPageView('Jury - Abstimmung');
    }, []);

    const assignedCount = Object.keys(poll).length;
    const totalCount = fundingOptions.length;
    const remainingCount = totalCount - assignedCount;
    const allAssigned = assignedCount === totalCount && totalCount > 0;

    // Projects that have a funding amount assigned, sorted by amount (highest first)
    const fundedProjects = projects.filter((p) => poll[p._id] !== undefined).sort((a, b) => poll[b._id] - poll[a._id]);

    // Quick lookup: amount → display name (e.g. 5000 → "Hauptpreis")
    const fundingDisplayByAmount = fundingOptions.reduce((acc, opt) => {
        acc[opt.amount] = opt.displayValue;
        return acc;
    }, {});

    const handleConfirm = async () => {
        setIsSubmitting(true);
        setError(null);
        try {
            await confirmVotes();
            setShowModal(false);
        } catch (err) {
            setError('Die Bestätigung ist fehlgeschlagen. Bitte versuchen Sie es erneut.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const SubmitSection = () => (
        <Container className={styles.SubmitSection}>
            <p className={`fw-bold ${styles.RemainingText}`} role="status" aria-live="polite">
                {remainingCount > 0
                    ? `Es sind noch ${remainingCount} von ${totalCount} Förderungen zu vergeben`
                    : `Alle ${totalCount} Förderungen vergeben`}
            </p>

            {isConfirmed ? (
                <p className={styles.ConfirmedText}>
                    <IconCheck aria-hidden="true" /> Ihre Förderungsvergabe wurde erfolgreich bestätigt.
                </p>
            ) : (
                <Button
                    color="primary"
                    className={styles.SubmitButton}
                    title={
                        !allAssigned
                            ? `Bitte vergeben Sie noch alle ${totalCount} Förderungen bevor Sie bestätigen.`
                            : undefined
                    }
                    disabled={!allAssigned}
                    onClick={() => setShowModal(true)}
                >
                    Förderungsvergabe bestätigen
                </Button>
            )}
        </Container>
    );

    return (
        <>
            <Helmet>
                <title>Jury-Voting - Abstimmung</title>
            </Helmet>

            <h1 className="visually-hidden">Jury-Voting Förderungsvergabe</h1>

            <SubmitSection />

            <JuryProjectsGrid
                projects={fundedProjects}
                showCount={false}
                emptyMessage="Weisen Sie Teilnehmern eine Förderung zu, indem Sie das Förderungsmenü auf der Teilnehmerkarte verwenden."
            />

            <SubmitSection />

            <Modal isOpen={showModal} toggle={() => !isSubmitting && setShowModal(false)}>
                <ModalHeader toggle={() => !isSubmitting && setShowModal(false)}>
                    Förderungsvergabe bestätigen
                </ModalHeader>
                <ModalBody>
                    <p>
                        Möchten Sie Ihre Förderungsvergabe wirklich abschließend bestätigen? Nach der Bestätigung können
                        keine Änderungen mehr vorgenommen werden.
                    </p>
                    <ListGroup flush>
                        {fundedProjects.map((p) => {
                            const amount = poll[p._id];
                            const label = fundingDisplayByAmount[amount] ?? `${amount?.toLocaleString('de-DE')} €`;
                            return (
                                <ListGroupItem key={p._id}>
                                    <strong className={styles.ContestantNameInModal}>{p.contestantName}</strong>
                                    {label}
                                </ListGroupItem>
                            );
                        })}
                    </ListGroup>
                    {error && (
                        <p className="text-danger" role="alert">
                            {error}
                        </p>
                    )}
                </ModalBody>
                <ModalFooter className={styles.ModalFooter}>
                    <Button
                        color="primary"
                        onClick={handleConfirm}
                        disabled={isSubmitting}
                        className={styles.ModalButton}
                    >
                        {isSubmitting ? 'Wird bestätigt…' : 'Ja, Förderungsvergabe bestätigen'}
                    </Button>
                    <Button
                        color="primary"
                        outline
                        onClick={() => setShowModal(false)}
                        disabled={isSubmitting}
                        className={styles.ModalButton}
                    >
                        Abbrechen
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
}

export default JuryFinalVoteSubmission;
